/** PDF writer for chat table exports.
 *
 *  Renders an extracted chat table (`components/Chat/Markdown/tableExtract.ts`)
 *  as a paginated A4 document: the user's question as a caption, the table as
 *  vector text with a repeating header, and the transcription caveat as a
 *  footer on every page.
 *
 *  Spec: docs/superpowers/specs/2026-09-22-chat-table-exports-design.md,
 *  "PDF rendering" and "Provenance" under Cross-cutting contracts.
 *
 *  NOT `html2pdf.js`, though it is already a dependency and `ArtifactDocCard`
 *  uses it. That is html2canvas + jsPDF: it screenshots the DOM and embeds the
 *  result as an image, so the output has no selectable text, nothing to Ctrl-F,
 *  no copyable figures, goes blurry on zoom and is large. It also slices page
 *  breaks through the middle of a row and repeats no header. All of that is
 *  acceptable for a rich document laid out in HTML; none of it is acceptable
 *  for a table someone opened this menu to pull a number out of.
 *
 *  KNOWN LIMIT — text outside WinAnsi (cp1252) does not survive. jsPDF's three
 *  built-in font families are WinAnsi-encoded, so `é`, `—`, `“ ”`, `€`, `£` and
 *  `¥` all render, but anything beyond that range (CJK, Cyrillic, `→`, and
 *  notably `₹`, which `coerce.ts` recognises as a currency symbol) is written
 *  into the content stream as raw UTF-16 bytes and reaches the reader as
 *  mojibake rather than as a missing glyph. Curing it means embedding a Unicode
 *  TTF — roughly a megabyte in this lazy chunk, plus a font-licensing call — so
 *  instead we DETECT it (`pdfCharset.ts`) and say so on the page: an extra
 *  footer paragraph, on every page, pointing at the spreadsheet export, which
 *  renders the whole of Unicode correctly. Do not "fix" it by stripping or
 *  substituting the offending characters: silently deleting or altering a value
 *  in an export is worse than rendering it badly, and the footer is only honest
 *  while the garbled value is still on the page to be checked against.
 */

import type { jsPDF } from "jspdf";
import type { UserOptions } from "jspdf-autotable";

import type { TableCell } from "./coerce";
import {
  UNRENDERABLE_CHARACTERS_WARNING,
  unrenderableCharactersIn,
  unrenderableCharactersInTable,
} from "./pdfCharset";
import {
  PROVENANCE_CAVEAT_LINES,
  TRUNCATION_WARNING,
  type ProvenanceInput,
} from "./provenance";

export const PDF_MIME = "application/pdf";

export interface PdfTableInput {
  /** Header cells, as `extractTable` produced them. Empty for a header-less table. */
  header: TableCell[];
  /** Body rows. */
  rows: TableCell[][];
  /** Where this table came from. The row count is derived from `rows`, so the
   *  caller cannot state one that disagrees with what is on the page. */
  provenance: Omit<ProvenanceInput, "rowCount">;
}

/** Past this many columns an A4 portrait page cannot hold the table at a
 *  readable size, so we turn the paper instead of shrinking the type. A
 *  12-column ERP table is an ordinary thing to ask for. */
const PORTRAIT_MAX_COLUMNS = 6;

/** Millimetres. The page is A4 in both orientations. */
const MARGIN = 14;
const CAPTION_FONT_PT = 11;
const TABLE_FONT_PT = 8;
const FOOTER_FONT_PT = 7;
/** Baseline of the footer's last line, measured up from the page edge. */
const FOOTER_BASELINE_FROM_BOTTOM = 10;
/** Clear air between the page's blocks — caption to table, table to footer rule. */
const BLOCK_GAP = 4;

/** Line height in the document's unit (mm here). jsPDF measures font size in
 *  points internally, so ask it rather than hard-coding a conversion that would
 *  silently go wrong the day a font size changes. */
function lineHeight(doc: jsPDF): number {
  return doc.getLineHeight() / doc.internal.scaleFactor;
}

function contentWidth(doc: jsPDF): number {
  return doc.internal.pageSize.getWidth() - MARGIN * 2;
}

/** Wrap a paragraph to the page, at the font size currently set on `doc`. */
function wrap(doc: jsPDF, text: string): string[] {
  return doc.splitTextToSize(text, contentWidth(doc));
}

/** Whether anything this document draws holds a character the built-in fonts
 *  cannot print — see `pdfCharset.ts`.
 *
 *  The caption is scanned as well as the cells. It is drawn on page 1 in the
 *  same fonts, so a table of perfectly renderable figures under a question
 *  rendered as mojibake is still a page that lies about itself. */
function hasUnrenderableText(input: PdfTableInput): boolean {
  return (
    unrenderableCharactersInTable(input).length > 0 ||
    unrenderableCharactersIn(input.provenance.question ?? "").length > 0
  );
}

/** The footer's paragraphs, in order: the specific facts about THIS document
 *  first, then the caveat that is true of every one of them.
 *
 *  The caveat's three constants are joined into one paragraph and handed to
 *  jsPDF to re-wrap for the actual page width — a line that fits the spec's
 *  plain-text layout does not fit A4 portrait at 7pt, and landscape wants
 *  different breaks again. Joining at the call site is the contract: the
 *  constant itself is never re-wrapped, re-flowed or re-worded, because the
 *  table's figures were retyped by a language model out of a truncated tool
 *  result and this paragraph is the only thing on the page that says so. */
function footerParagraphs(input: PdfTableInput): string[] {
  const paragraphs: string[] = [];
  if (input.provenance.truncated) paragraphs.push(TRUNCATION_WARNING);
  if (hasUnrenderableText(input)) paragraphs.push(UNRENDERABLE_CHARACTERS_WARNING);
  paragraphs.push(PROVENANCE_CAVEAT_LINES.join(" "));
  return paragraphs;
}

/** Draw the footer on whatever page is current, and report its height so the
 *  table's bottom margin can reserve the space. */
function drawFooter(doc: jsPDF, lines: string[]): void {
  const pageHeight = doc.internal.pageSize.getHeight();

  doc.setFont("helvetica", "normal");
  doc.setFontSize(FOOTER_FONT_PT);

  const step = lineHeight(doc);
  const firstBaseline =
    pageHeight - FOOTER_BASELINE_FROM_BOTTOM - (lines.length - 1) * step;

  // A hairline rule, so the caveat cannot be mistaken for a final table row.
  doc.setDrawColor(200);
  doc.setLineWidth(0.2);
  doc.line(
    MARGIN,
    firstBaseline - step,
    doc.internal.pageSize.getWidth() - MARGIN,
    firstBaseline - step,
  );

  doc.setTextColor(110);
  doc.text(lines, MARGIN, firstBaseline);
  doc.setTextColor(0);
}

/** Height to reserve at the foot of every page for `drawFooter`'s output. */
function footerHeight(doc: jsPDF, lineCount: number): number {
  doc.setFontSize(FOOTER_FONT_PT);
  const step = lineHeight(doc);
  return FOOTER_BASELINE_FROM_BOTTOM + lineCount * step + BLOCK_GAP;
}

/** Draw the caption above the table; returns the y the table should start at.
 *
 *  Page 1 only. The question is what the table is an answer to, so it reads as
 *  the document's title; the caveat, which has to survive someone printing page
 *  3 on its own, is the part that repeats. */
function drawCaption(doc: jsPDF, question: string | null): number {
  const text = question?.trim();
  if (!text) return MARGIN;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(CAPTION_FONT_PT);
  doc.setTextColor(0);

  const lines = wrap(doc, text);
  const step = lineHeight(doc);
  const firstBaseline = MARGIN + step;
  doc.text(lines, MARGIN, firstBaseline);

  return firstBaseline + (lines.length - 1) * step + BLOCK_GAP;
}

/** Widest row wins. `extractTable` pads its output to a rectangle, but a caller
 *  that does not would otherwise get portrait on the strength of a short header
 *  row while its data runs off the side of the page. */
function columnCount(input: PdfTableInput): number {
  return input.rows.reduce((width, row) => Math.max(width, row.length), input.header.length);
}

function textOf(cells: TableCell[]): string[] {
  return cells.map((cell) => cell.text);
}

/**
 * Render the table into a jsPDF document.
 *
 * Cells are written as the reader saw them — `coerceCell` is deliberately NOT
 * applied. A PDF is a picture of the answer, not a working copy: it holds no
 * formulas, so a coerced `1234.56` would have to be formatted back to
 * `$1,234.56` through an Excel format code to print, and the lossy edges of
 * coercion (`—` becoming empty, a long invoice number staying text) would make
 * the page disagree with the chat it claims to be evidence of. The spreadsheet
 * is where typed cells earn their keep; see `xlsx.ts`.
 *
 * Exported alongside `buildTablePdf` because the document — its page count, its
 * content streams, autotable's resolved settings — is the only honest way to
 * assert what was rendered without comparing pixels.
 */
export async function renderTablePdf(input: PdfTableInput): Promise<jsPDF> {
  // Lazy, mirroring `ArtifactDocCard.tsx:27`: between them these two are a
  // sizeable chunk of JS, and nobody pays for it until they click Export.
  const [{ jsPDF: JsPDF }, { default: autoTable }] = await Promise.all([
    import("jspdf"),
    import("jspdf-autotable"),
  ]);

  const landscape = columnCount(input) > PORTRAIT_MAX_COLUMNS;
  const doc = new JsPDF({
    orientation: landscape ? "landscape" : "portrait",
    unit: "mm",
    format: "a4",
  });

  doc.setFontSize(FOOTER_FONT_PT);
  const footerLines = footerParagraphs(input).flatMap((paragraph) => wrap(doc, paragraph));
  const reserved = footerHeight(doc, footerLines.length);

  const startY = drawCaption(doc, input.provenance.question);

  const options: UserOptions = {
    head: input.header.length > 0 ? [textOf(input.header)] : [],
    body: input.rows.map(textOf),
    startY,
    margin: { top: MARGIN, left: MARGIN, right: MARGIN, bottom: reserved },
    theme: "grid",
    // The two halves of "page breaks on row boundaries, header repeated".
    showHead: "everyPage",
    rowPageBreak: "avoid",
    styles: {
      font: "helvetica",
      fontSize: TABLE_FONT_PT,
      cellPadding: 1.8,
      overflow: "linebreak",
      lineColor: [220, 220, 220],
      lineWidth: 0.1,
      textColor: [20, 20, 20],
    },
    headStyles: { fillColor: [51, 51, 51], textColor: 255, fontStyle: "bold" },
    // Fires once per page the table touches — which is every page, since the
    // table is the only thing on the document after the caption.
    didDrawPage: () => drawFooter(doc, footerLines),
  };

  autoTable(doc, options);

  return doc;
}

/** The table as a downloadable PDF file. */
export async function buildTablePdf(input: PdfTableInput): Promise<Blob> {
  const doc = await renderTablePdf(input);
  return doc.output("blob");
}
